import AnimatedSymbolCard from "features/watchlist/WatchlistCards/AnimatedSymbolCard";
import React, { useRef, useState } from "react";
import useMedia from "utils/hooks/useMedia";
import { NEW_LOOK_NARROW_PHONE_QUERY } from "common/NewLook/layout.ts";
import { watchlistService } from "app/services/watchlist.service";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay";
import { CardPosition, Market } from "types";
import { arrayMoveMutable } from "array-move";
import { findIndex } from "features/watchlist/common/utils";
import { createMoveCardRequest } from "features/watchlist/common/utils";
import { motion } from "motion/react";
import Empty from "common/Empty/Empty";
import EqualColumnsGrid from "common/Grids/EqualColumnsGrid";
import { useTheme } from "styled-components";
import Link from "common/Typography/Link";
import styled from "styled-components";
import Flex from "common/Boxes/Flex";
import { MOBILE_SEARCH } from "common/constants";
import { useAppDispatch } from "app/hooks";
import { CoinName, ExchangeName, LanguageNameSpaces } from "@shared/types";
import EmptyView from "common/Empty/EmptyView";
import useThrottledCallback from "@shared/utils/hooks/useThrottledCallback";
import { EmptyStatesType } from "common/Icons/types.ts";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation.ts";
import Panel from "common/NewLook/Panel.tsx";
import { useBottomNavigator } from "utils/hooks/misc";

const MIN_PREVIEW_CARDS = 2;

interface WatchlistCardsProps {
  onRemove: (symbol: CoinName, exchange: ExchangeName) => void;
}

const WatchlistCards = ({ onRemove }: WatchlistCardsProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.WATCHLIST);
  const dispatch = useAppDispatch();
  const isMobile = useMedia([`(max-width: ${MOBILE_SEARCH})`], [true], false);
  const [showAll, setShowAll] = useState(isMobile);
  const appTheme = useTheme();
  const constraintsRef = useRef(null);
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();
  const showNewLookHeader = appTheme.isNewLook && !isBottomNavigator;

  const numCardsInRow = useMedia(
    [
      ...(appTheme.isNewLook ? [NEW_LOOK_NARROW_PHONE_QUERY] : []),
      "(max-width: 850px)",
      "(max-width: 1275px)",
      "(max-width: 1450px)",
      "(max-width: 1700px)",
      "(max-width: 2000px)",
    ],
    [...(appTheme.isNewLook ? [1] : []), 2, 3, 4, 5, 6],
    6
  );

  let {
    data: watchlist,
    isFetching,
    isSuccess,
    isError,
  } = watchlistService.useGetWatchlistQuery();
  const { sortedWatchlist } = watchlist || {};
  const prevArgsRef = useRef("");

  const [moveWatchlistCard] = watchlistService.useMoveWatchlistCardMutation();

  const positions = useRef<CardPosition[]>([]);

  const getMarketItemKey = (item: Market) => `${item.symbol}-${item.exchange}`;

  const setPosition = (i: number, offset: CardPosition) =>
    (positions.current[i] = offset);

  const moveItem = useThrottledCallback({
    callback: (i: number, yOffset: number, xOffset: number) => {
      if (positions.current.length) {
        const targetIndex = findIndex(
          i,
          yOffset,
          xOffset,
          positions.current,
          numCardsInRow
        );
        const argsStr = `${i}_${targetIndex}`;
        if (targetIndex !== i && argsStr !== prevArgsRef.current && watchlist) {
          prevArgsRef.current = argsStr;
          dispatch(
            watchlistService.util.updateQueryData(
              "getWatchlist",
              undefined,
              (previousWatchlist) => {
                arrayMoveMutable(
                  previousWatchlist.sortedWatchlist,
                  i,
                  targetIndex
                );
              }
            )
          );
        }
      }
    },
    wait: 50,
    options: { trailing: true },
  });

  const replaceItems = (i: number) => {
    if (sortedWatchlist)
      moveWatchlistCard(createMoveCardRequest(i, sortedWatchlist));
  };

  const addEmptyWatchlistMessage = () => {
    return (
      <EmptyView
        iconKey={EmptyStatesType.Coins}
        title={t("empty.title")}
        description={t("empty.description")}
        iconWidth={140}
      />
    );
  };

  if ((isSuccess && !sortedWatchlist) || isError) {
    return (
      <NoDataWrapper>
        <NoData />
      </NoDataWrapper>
    );
  }

  let filteredWatchlist = sortedWatchlist;
  if (!showAll) {
    filteredWatchlist = sortedWatchlist?.slice(
      0,
      Math.max(numCardsInRow, MIN_PREVIEW_CARDS)
    );
  }
  const displayShowAllButton =
    !showAll &&
    (filteredWatchlist?.length || 0) < (sortedWatchlist?.length || 0);
  const gap = isMobile ? 12 : 16;

  const showAllButton = displayShowAllButton && (
    <ShowAllButton onClick={() => setShowAll(true)}>
      {t("buttons.show_all")}
    </ShowAllButton>
  );

  const content = (
    <>
      <ExtendedSymbolCardsWrapper>
        <LoadingSpinnerOverlay
          isLoading={isFetching}
          spinnerMarginTop={100}
          backgroundColor={appTheme.colors.layoutBgColor}
          overlayOpacity={0.6}
        >
          {isSuccess && !sortedWatchlist?.length && addEmptyWatchlistMessage()}
          <motion.div ref={constraintsRef}>
            <EqualColumnsGrid
              numColumns={numCardsInRow}
              rowGap={gap}
              columnGap={gap}
            >
              {filteredWatchlist?.map((market, i) => (
                <div
                  key={getMarketItemKey(market)}
                  id={
                    sortedWatchlist?.length && i === sortedWatchlist?.length - 1
                      ? "last-watchlist-item"
                      : undefined
                  }
                >
                  <AnimatedSymbolCard
                    key={getMarketItemKey(market)}
                    watchlistMarket={market}
                    onRemove={onRemove}
                    i={i}
                    setPosition={setPosition}
                    moveItem={moveItem}
                    replaceItems={replaceItems}
                    constraintsRef={constraintsRef}
                  />
                </div>
              ))}
            </EqualColumnsGrid>
          </motion.div>
          {displayShowAllButton && !showNewLookHeader && (
            <Flex $justifyContent="center">{showAllButton}</Flex>
          )}
        </LoadingSpinnerOverlay>
      </ExtendedSymbolCardsWrapper>
    </>
  );

  // The page's lead: the featured symbols sit in the hero panel.
  if (showNewLookHeader)
    return (
      <Panel
        title={t("tabs.watchlist")}
        count={sortedWatchlist?.length || undefined}
        actions={
          showAllButton && <NewLookShowAll>{showAllButton}</NewLookShowAll>
        }
      >
        {content}
      </Panel>
    );
  return content;
};

export default WatchlistCards;

const ExtendedSymbolCardsWrapper = styled.div`
  width: 100%;
  position: relative;
  min-height: ${(p) => (p.theme.isNewLook ? 120 : 174)}px;
`;
const NoData = styled(Empty)`
  margin-top: 64px;
`;
const NoDataWrapper = styled.div`
  width: 100%;
`;
const ShowAllButton = styled(Link)`
  padding: 16px 16px 0 16px;
`;
const NewLookShowAll = styled.span`
  a,
  span {
    padding: 0;
    font-weight: ${(p) => p.theme.typography.fontWeights.MEDIUM};
  }
`;
